/*
  Hero 3D : le motif imprime (loup et sept drapeaux), recompose en particules.

  Deroule :
  - un nuage se forme en motif, de la tete vers le bas des drapeaux ;
  - ensuite le motif vit seul : il tourne lentement sur lui-meme, les drapeaux
    flottent, leurs franges s'effilochent, des braises montent, et une vague
    de lumiere le parcourt toutes les quelques secondes ;
  - au defilement, les particules se dispersent et s'eteignent.

  Rien ne reagit a la souris : le mouvement est autonome.

  Garde-fous :
  - rien ne se charge avant que la section approche de l'ecran ;
  - animations reduites demandees : le motif s'affiche forme, immobile ;
  - economie de donnees, connexion lente, ou WebGL absent : image fixe ;
  - si l'appareil peine, le halo saute, puis la resolution baisse ;
  - pause des que le hero sort de l'ecran ou que l'onglet est masque.
*/

(function () {
  'use strict';

  /* --- Relief du motif ------------------------------------------------ */
  // Coordonnees u, v de l'image source (0..1, v vers le bas), calees sur le
  // dessin : truffe en (0.505, 0.345), yeux vers v = 0.226, bas de la
  // machoire a 0.41. Les profondeurs sont exprimees en largeur de motif.

  function inHead(u, v) {
    if (v > 0.415) return false;
    if (v > 0.27 && Math.abs(u - 0.505) > 0.25) return false;
    var du = (u - 0.505) / 0.4, dv = (v - 0.2) / 0.23;
    return du * du + dv * dv < 1.25;
  }

  function inEye(u, v) {
    var a = (u - 0.387) / 0.045, b = (u - 0.633) / 0.045, c = (v - 0.226) / 0.02;
    return a * a + c * c < 1 || b * b + c * c < 1;
  }

  function zHead(u, v) {
    var du = (u - 0.505) / 0.38, dv = (v - 0.24) / 0.25, e = 1 - du * du - dv * dv;
    var z = e > 0 ? 0.15 * Math.sqrt(e) : 0;
    z += 0.08 * Math.exp(-(((u - 0.505) * (u - 0.505)) / 0.005 + ((v - 0.335) * (v - 0.335)) / 0.006));
    return z;
  }

  function zFlags(u, v) {
    return 0.05 * Math.cos((u - 0.5) * 3.2) - 0.03 + 0.01 * Math.sin(u * 46 + v * 4) - 0.06 * Math.max(0, v - 0.45);
  }

  function saturation(r, g, b) {
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    return mx > 0 ? (mx - mn) / mx : 0;
  }

  // Fourrure eclaircie, yeux ambres renforces pour qu'ils brillent.
  function tint(r, g, b, u, v) {
    if (inEye(u, v) && saturation(r, g, b) > 0.25) return [r * 1.6 + 0.1, g * 1.25 + 0.03, b * 0.5];
    var k = saturation(r, g, b) < 0.3 ? 1.4 : 1.15;
    return [Math.min(1, r * k), Math.min(1, g * k), Math.min(1, b * k)];
  }

  // Le motif en couleurs, plus une fine nuee qui remplit la silhouette pour
  // donner du volume quand il tourne.
  function designShape(Z, px, sil, n, height) {
    var width = height * (px.w / px.h);
    var nFill = Math.round(n * 0.1);

    var main = Z.fromPixels(px, n - nFill, {
      weight: function (r, g, b, a) { return Math.pow(a, 1.4); },
      place: function (u, v, r, g, b, a, rand, out) {
        var head = inHead(u, v) && saturation(r, g, b) < 0.36;
        var z = (head ? zHead(u, v) : zFlags(u, v)) + (rand() - 0.5) * 0.02;
        var c = tint(r, g, b, u, v);
        out[0] = (u - 0.5) * width; out[1] = (0.5 - v) * height; out[2] = z * width;
        out[3] = c[0]; out[4] = c[1]; out[5] = c[2];
      }
    }, 3);

    var d = px.d;
    var fill = Z.fromPixels(sil, nFill, {
      weight: function (r) { return r > 0.5 ? 1 : 0; },
      place: function (u, v, r, g, b, a, rand, out) {
        var k = (Math.min(px.h - 1, Math.floor(v * px.h)) * px.w + Math.min(px.w - 1, Math.floor(u * px.w))) * 4;
        var z = (inHead(u, v) ? zHead(u, v) * 0.75 : zFlags(u, v)) + (rand() - 0.5) * 0.05;
        var c = tint(d[k] / 255, d[k + 1] / 255, d[k + 2] / 255, u, v);
        out[0] = (u - 0.5) * width; out[1] = (0.5 - v) * height; out[2] = z * width;
        out[3] = c[0] * 0.3 + 0.02; out[4] = c[1] * 0.3 + 0.02; out[5] = c[2] * 0.3 + 0.025;
      }
    }, 5);

    return Z.concat([main, fill], 9);
  }

  function clamp(v, a, b) {
    return Math.max(a, Math.min(b, v));
  }

  /* --- Composant ------------------------------------------------------ */

  var HEIGHT = 3.3;         // hauteur du motif, en unites de scene
  var SWEEP_EVERY = 7;      // secondes entre deux vagues de lumiere
  var SWEEP_LENGTH = 2.4;   // duree d'une vague

  class ZkHero3d extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('[data-hero3d-canvas]');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.watchTopOffset();
      this.startRotator();

      if (!this.canvas || !window.ZKParticles || this.shouldUseImage()) {
        this.fallback();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          this.visible = entries.some((entry) => entry.isIntersecting);
          if (this.visible && !this.engine && !this.loading) this.boot();
          this.updateRunning();
        },
        { rootMargin: '100px 0px' }
      );
      this.observer.observe(this);

      this.onVisibility = () => this.updateRunning();
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.measureTopOffset) {
        window.removeEventListener('resize', this.measureTopOffset);
        window.removeEventListener('load', this.measureTopOffset);
      }
      clearInterval(this.rotatorTimer);
      if (this.engine) this.engine.destroy();
      this.engine = null;
    }

    /*
      Meme mesure que le hero precedent : la section commence sous la barre
      d'annonce et l'en-tete. On retranche leur hauteur pour que le hero tienne
      exactement dans le premier ecran.
    */
    watchTopOffset() {
      this.measureTopOffset = () => {
        const valeur = Math.max(0, Math.round(this.getBoundingClientRect().top + window.scrollY));
        if (valeur === this.topOffset) return;
        this.topOffset = valeur;
        this.style.setProperty('--hero3d-top-offset', valeur + 'px');
      };
      this.measureTopOffset();
      requestAnimationFrame(this.measureTopOffset);
      window.addEventListener('resize', this.measureTopOffset);
      window.addEventListener('load', this.measureTopOffset, { once: true });
    }

    shouldUseImage() {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (!connection) return false;
      if (connection.saveData) return true;
      return /(^|-)2g$/.test(connection.effectiveType || '');
    }

    fallback() {
      this.classList.add('zk-hero3d--image');
      const img = this.querySelector('[data-hero3d-fallback]');
      if (img && img.dataset.src && !img.getAttribute('src')) img.src = img.dataset.src;
      if (this.canvas) this.canvas.hidden = true;
    }

    /* --- Mot qui tourne dans le titre --------------------------------- */

    startRotator() {
      const words = Array.from(this.querySelectorAll('[data-hero3d-word]'));
      if (words.length < 2 || this.reduced) return;
      let index = 0;
      this.rotatorTimer = setInterval(() => {
        if (document.hidden) return;
        const current = words[index];
        index = (index + 1) % words.length;
        const next = words[index];
        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        next.classList.remove('is-leaving');
        next.classList.add('is-active');
        setTimeout(() => current.classList.remove('is-leaving'), 700);
      }, 2600);
    }

    /* --- Scene -------------------------------------------------------- */

    async boot() {
      this.loading = true;
      const Z = window.ZKParticles;
      try {
        this.engine = new Z.Engine(this.canvas, { maxDpr: window.innerWidth < 750 ? 1.5 : 1.75 });
        const sources = await Promise.all([Z.loadPixels(this.dataset.design), Z.loadPixels(this.dataset.silhouette)]);
        this.build(Z, sources[0], sources[1]);
      } catch (error) {
        if (this.engine) this.engine.destroy();
        this.engine = null;
        this.fallback();
        return;
      }
      this.loading = false;
      this.classList.add('zk-hero3d--ready');

      this.onResize = () => {
        if (!this.engine) return;
        this.engine.resize();
        if (this.reduced) this.renderStill();
      };
      window.addEventListener('resize', this.onResize);

      if (this.reduced) this.renderStill();
      else this.updateRunning();
    }

    quality() {
      const w = window.innerWidth;
      const base = w < 750 ? 0.42 : w < 1100 ? 0.7 : 1;
      const density = parseFloat(this.dataset.density || '100') / 100;
      return base * clamp(density, 0.3, 1.2);
    }

    build(Z, design, silhouette) {
      const E = this.engine;
      const q = this.quality();
      const reduced = this.reduced;
      const n = Math.round(52000 * q);

      this.dust = E.add(Math.round(1700 * q), { uniforms: { size: 0.028, twinkle: 0.8, noise: 0.02, alpha: 0.75 } });
      this.dust.show(Z.dust(this.dust.n, 9, 8));

      this.embers = null;
      if (this.dataset.embers === 'true' && !reduced) {
        this.embers = E.add(Math.round(380 * q) + 90, {
          halo: [3.2, 0.35, 1],
          uniforms: { size: 0.036, alpha: 0, twinkle: 0.85, noise: 0.08, rise: 0.22, riseRange: 5.4, soft: 2 },
          seed: 41
        });
        this.embers.show(Z.embers(this.embers.n, 9, 5.4, 43));
      }

      this.art = E.add(n, {
        halo: [4, 0.2, 0.35],
        uniforms: {
          size: 0.0102, alpha: 0, noise: reduced ? 0 : 0.007, swirl: 2.4, twinkle: reduced ? 0 : 0.22,
          delaySpan: 0.5, swayTop: HEIGHT * 0.09, dripTop: -HEIGHT * 0.12,
          scanColor: [0.85, 0.66, 0.48], scanPush: 0.26
        }
      });

      const shape = designShape(Z, design, silhouette, n, HEIGHT);
      if (reduced) {
        this.art.show(shape);
        this.art.u.alpha = 0.95;
      } else {
        this.art.show(Z.nebula(n, 3.4, 51));
        this.art.morphTo(shape, 3.4, {
          order: (x, y, z, r) => (1 - (y + HEIGHT / 2) / HEIGHT) * 0.82 + r * 0.18
        });
      }

      E.onFrame = (t) => this.animate(t);
    }

    // Placement selon le format : a droite du texte sur grand ecran, au-dessus
    // sur telephone. Hors axe, le motif se tourne vers la camera pour rester de face.
    layout() {
      const E = this.engine, art = this.art;
      const aspect = E.width / E.height;
      if (aspect < 0.85) {
        art.position = [0, 0.8, 0];
        art.scale = 0.6;
      } else {
        art.position = [1.4 + (aspect - 1.78) * 0.45, -0.05, 0];
        art.scale = 1;
      }
      return -Math.atan2(art.position[0], E.camera.z);
    }

    scrollProgress() {
      const rect = this.getBoundingClientRect();
      return clamp(-rect.top / Math.max(1, rect.height), 0, 1);
    }

    animate(t) {
      const E = this.engine, art = this.art, u = art.u;
      const face = this.layout();
      const sp = this.scrollProgress();
      const fade = 1 - sp * 0.9;

      // Entree : le nuage apparait, puis le mouvement permanent s'installe.
      u.alpha = Math.min(0.95, t * 1.2) * fade;
      const settled = clamp((t - 2.6) / 2, 0, 1);
      u.sway = 0.09 * settled;
      u.drip = 0.1 * clamp((t - 3.4) / 2, 0, 1);

      // Rotation lente, leger flottement vertical, respiration.
      art.rotation[1] = face + Math.sin(t * 0.26) * 0.34;
      art.rotation[0] = Math.sin(t * 0.19) * 0.04;
      art.position[1] += Math.sin(t * 0.45) * 0.05;
      art.scale *= 1 + Math.sin(t * 0.7) * 0.012;

      // Vague de lumiere, du haut vers le bas, a intervalle regulier.
      const cycle = t - 4.5;
      if (cycle > 0 && cycle % SWEEP_EVERY < SWEEP_LENGTH) {
        const k = (cycle % SWEEP_EVERY) / SWEEP_LENGTH;
        u.scanY = HEIGHT / 2 + 0.4 - k * (HEIGHT + 0.8);
        u.scanF = Math.sin(k * Math.PI);
      } else {
        u.scanF = 0;
      }

      u.scatter = sp * 2.6;
      this.dust.u.alpha = 0.75 * (1 - sp);
      if (this.embers) this.embers.u.alpha = Math.min(0.75, Math.max(0, t - 1) * 0.5) * (1 - sp * 0.6);

      E.camera.z = 6 + sp * 1.4;
      E.camera.yaw = Math.sin(t * 0.21) * 0.035;

      this.adaptQuality(t);
    }

    // Une seule fois, apres l'entree : si l'appareil peine, on allege.
    adaptQuality(t) {
      if (this.adapted || t < 5) return;
      this.adapted = true;
      const frame = this.engine.averageFrame();
      if (frame > 0.026) this.engine.halos = false;
      if (frame > 0.04) {
        this.engine.maxDpr = 1;
        this.engine.resize();
      }
    }

    renderStill() {
      const face = this.layout();
      this.art.rotation[1] = face;
      this.engine.draw();
    }

    updateRunning() {
      if (!this.engine || this.loading || this.reduced) return;
      if (this.visible && !document.hidden) this.engine.start();
      else this.engine.stop();
    }
  }

  if (!customElements.get('zk-hero3d')) customElements.define('zk-hero3d', ZkHero3d);
})();
